import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  clearUserSession,
  getUserDisplayName,
  getUserSession,
} from '../../services/authService';

const menuItems = [
  { id: 'perfil', label: 'Perfil', path: '/perfil', icon: 'user' },
  { id: 'pacientes', label: 'Pacientes', path: '/perfil/pacientes', icon: 'footprint', onlyFor: 'paciente' },
  { id: 'notificaciones', label: 'Notificaciones', path: '/perfil/notificaciones', icon: 'bell' },
  { id: 'password', label: 'Cambiar contraseña', path: '/perfil/cambiar-contraseña', icon: 'lock' },
  { id: 'subscriptions', label: 'Planes de Suscripción', path: '/perfil/planes', icon: 'puzzle' },
  { id: 'language', label: 'Lenguaje', path: '/perfil/idioma', icon: 'language' },
  { id: 'terms', label: 'Términos y condiciones', path: '/perfil/terminos', icon: 'terms' },
  { id: 'ficha', label: 'Ficha', path: '/perfil/ficha', icon: 'review', onlyFor: ['especialista', 'administrativo'] },
  { id: 'logout', label: 'Cerrar Sesión', icon: 'logout', action: 'logout' },
];

const SidebarIcon = ({ name }) => {
  switch (name) {
    case 'user':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
        </svg>
      );
    case 'footprint':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 18c1.5-2 2-4 2-6a3 3 0 016 0c0 2 .5 4 2 6" />
          <path d="M6 14c1-1.5 1.5-3 1.5-4.5a2.5 2.5 0 015 0C12.5 11 13 12.5 14 14" />
        </svg>
      );
    case 'bell':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
          <path d="M13.7 21a2 2 0 01-3.4 0" />
        </svg>
      );
    case 'lock':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 118 0v3" />
        </svg>
      );
    case 'puzzle':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 4h2a2 2 0 012-2h0a2 2 0 012 2v2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2H6a2 2 0 01-2-2V8a2 2 0 012-2h2V4z" />
        </svg>
      );
    case 'language':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18" />
        </svg>
      );
    case 'terms':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 4h7l3 3v13H7z" />
          <path d="M14 4v4h4M9 12h6M9 16h6" />
        </svg>
      );
    case 'privacy':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 4h10a2 2 0 012 2v14H8a2 2 0 01-2-2V4z" />
          <path d="M6 8h12" />
        </svg>
      );
    case 'review':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 4h7l3 3v13H7z" />
          <path d="M14 4v4h4M9 12h6M9 16h4" />
        </svg>
      );
    case 'logout':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 17l-1 1H5a1 1 0 01-1-1v-4" />
          <path d="M15 12H8m7 0l-3-3m3 3l-3 3" />
          <path d="M19 5v14" />
        </svg>
      );
    default:
      return null;
  }
};

const ProfileSidebar = ({ activeId = 'perfil' }) => {
  const history = useHistory();
  const location = useLocation();
  const sessionUser = getUserSession();
  const displayName = getUserDisplayName(sessionUser);
  const firstName = (displayName.split(/\s+/)[0] || 'Usuario').trim();
  const visibleMenuItems = menuItems.filter((item) => {
    if (
      item.id === 'language' &&
      sessionUser &&
      sessionUser.tipo === 'paciente'
    ) {
      return false;
    }

    if (item.hideFor && sessionUser && sessionUser.tipo === item.hideFor) {
      return false;
    }

    if (item.onlyFor && sessionUser) {
      const permitidos = Array.isArray(item.onlyFor) ? item.onlyFor : [item.onlyFor];
      return permitidos.indexOf(sessionUser.tipo) !== -1;
    }

    return true;
  });

  const handleItemClick = (item) => {
    if (item.action === 'logout') {
      clearUserSession();
      history.push('/');
      return;
    }

    if (item.disabled) {
      return;
    }

    if (item.path) {
      history.push(item.path);
    }
  };

  const isActive = (item) => {
    if (item.id === activeId) {
      return true;
    }

    if (item.path && location.pathname === item.path) {
      return true;
    }

    return false;
  };

  return (
    <aside className="profile-sidebar">
      <h2 className="profile-sidebar-greeting">¡Hola, {firstName}!</h2>

      <nav className="profile-sidebar-nav" aria-label="Menú de perfil">
        {visibleMenuItems.map((item) => {
          const active = isActive(item);

          return (
            <button
              key={item.id}
              type="button"
              className={`profile-sidebar-item${active ? ' profile-sidebar-item--active' : ''}${item.disabled ? ' profile-sidebar-item--disabled' : ''}`}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
            >
              <span className="profile-sidebar-icon">
                <SidebarIcon name={item.icon} />
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default ProfileSidebar;
